import { ColumnKey } from './models';

/**
 * Error message patterns that indicate a non-retriable failure.
 * These are validation rejections from the API — retrying won't change the outcome.
 */
export const NON_RETRIABLE_ERROR_PATTERNS = [
    'residential',
    'file size exceeds',
    'exceeds maximum page limit',
];

export const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
    status: true,
    provider: true,
    utility: true,
    type: true,
    legalEntity: true,
    utilityAccountNumber: true,
    utilityAccountNumber2: false,
    startDate: true,
    endDate: true,
    usage: true,
    amount: true,
    confidence: true,
    actions: true,
    supplyCharges: false,
    supplyTaxes: false,
    distributionCharges: false,
    distributionTaxes: false,
    fileName: false,
    supplyContractCharges: false,
    supplyEnergyCharges: false,
    supplyCapacityCharges: false,
    supplyTransmissionCharges: false,
    supplyMarketCharges: false,
    supplyLineLossCharges: false,
    supplyGrtSut: false,
    supplyMiscCharges: false,
    supplySalesTaxes: false,
    distributionDemandCharges: false,
    distributionEnergyCharges: false,
    distributionCustomerCharges: false,
    distributionMiscCharges: false,
    distributionTaxCharges: false,
    warnings: false,
};

export const PRO_MODE_COLUMNS = new Set([
  'supplyContractCharges',
  'supplyEnergyCharges',
  'supplyCapacityCharges',
  'supplyTransmissionCharges',
  'supplyMarketCharges',
  'supplyLineLossCharges',
  'supplyGrtSut',
  'supplyMiscCharges',
  'supplySalesTaxes',
  'distributionDemandCharges',
  'distributionEnergyCharges',
  'distributionCustomerCharges',
  'distributionMiscCharges',
  'distributionTaxCharges'
]);

export const DEFAULT_BUDGETING_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
    ...DEFAULT_VISIBLE_COLUMNS,
    ...Object.fromEntries([...PRO_MODE_COLUMNS].map(key => [key, true]))
} as Record<ColumnKey, boolean>;

/** Column groups for the selector left panel (standard columns) */
export const SELECTOR_STANDARD_GROUPS: { title: string; columns: ColumnKey[] }[] = [
    {
        title: "General",
        columns: ['status', 'legalEntity', 'provider', 'utility', 'fileName', 'type', 'confidence', 'warnings']
    },
    {
        title: "Account & Dates",
        columns: ['utilityAccountNumber', 'utilityAccountNumber2', 'startDate', 'endDate']
    },
    {
        title: "Usage & Totals",
        columns: ['usage', 'amount', 'supplyCharges', 'supplyTaxes', 'distributionCharges', 'distributionTaxes']
    },
];

/** Column groups for the selector right panel (budgeting breakdown columns) */
export const SELECTOR_BUDGETING_GROUPS: { title: string; columns: ColumnKey[] }[] = [
    {
        title: "Supply Breakdown",
        columns: [
            'supplyContractCharges',
            'supplyEnergyCharges',
            'supplyCapacityCharges',
            'supplyTransmissionCharges',
            'supplyMarketCharges',
            'supplyLineLossCharges',
            'supplyGrtSut',
            'supplyMiscCharges',
            'supplySalesTaxes'
        ]
    },
    {
        title: "Distribution Breakdown",
        columns: [
            'distributionDemandCharges',
            'distributionEnergyCharges',
            'distributionCustomerCharges',
            'distributionMiscCharges',
            'distributionTaxCharges'
        ]
    },
];

/** Column groups used for table header rendering (all columns in display order) */
export const COLUMN_GROUPS: { title: string; columns: ColumnKey[] }[] = [
    {
        title: "General Information",
        columns: ['status', 'legalEntity', 'provider', 'utility', 'fileName', 'type', 'confidence']
    },
    {
        title: "Account & Dates",
        columns: ['utilityAccountNumber', 'utilityAccountNumber2', 'startDate', 'endDate']
    },
    {
        title: "Usage & Totals",
        columns: ['usage', 'amount']
    },
    {
        title: "Supply Details",
        columns: [
            'supplyCharges', 
            'supplyTaxes',
            'supplyContractCharges', 
            'supplyEnergyCharges', 
            'supplyCapacityCharges', 
            'supplyTransmissionCharges', 
            'supplyMarketCharges', 
            'supplyLineLossCharges', 
            'supplyGrtSut', 
            'supplyMiscCharges', 
            'supplySalesTaxes'
        ]
    },
    {
        title: "Distribution Details",
        columns: [
            'distributionCharges', 
            'distributionTaxes',
            'distributionDemandCharges', 
            'distributionEnergyCharges', 
            'distributionCustomerCharges', 
            'distributionMiscCharges', 
            'distributionTaxCharges'
        ]
    },
    {
        title: "Actions",
        columns: ['actions']
    }
];
